import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import Container from 'components/organisms/Container';
import Section from 'components/organisms/Section';
import NewsListHome from 'components/templates/Home/NewsListHome';
import i18n from 'i18n';
import { getNewsEventListService } from 'services/news';
import { getDisclosuresService } from 'services/news/disclosures';
import getSGDCKNewsDataService from 'services/news/sgdckNews';
import { useAppSelector } from 'store/hooks';
import { DEFAULT_QUERY_OPTION } from 'utils/constants';
import { formatDateDDMMYYYY } from 'utils/functions';
import { getStaticURL } from 'utils/language';

export interface NewsListBlocks {
  title: string;
  link: {
    text: string;
    url: string;
    target: string;
  };
}

interface NewsListBlock {
  blocks: NewsListBlocks;
  // newsList?: NewsCardProps[];
  radioList: {
    value: string;
    title: string;
  }[],
  selectedType: string;
  handleSetSelectedType: (type: string) => void;
}

const NewsList: React.FC<NewsListBlock> = ({
  blocks,
  // newsList,
  radioList,
  selectedType,
  handleSetSelectedType,
}) => {
  const { t } = useTranslation();
  const language = useAppSelector((state) => state.system.language);

  const {
    isLoading: newsLoading,
    // error: newsError,
    data: newsData,
  } = useQuery(
    ['GetHomeNews', i18n.language],
    () => getNewsEventListService({ limit: 10, outstanding: 1 }),
    {
      ...DEFAULT_QUERY_OPTION,
      refetchOnMount: true,
      enabled: selectedType === 'news',
    },
  );

  const {
    isLoading: newsDisclousureLoading,
    // error: newsDisclousureError,
    data: newsDisclousureData,
  } = useQuery(
    ['GetHomeNewsDisclousure', i18n.language],
    () => getDisclosuresService({ limit: 10, outstanding: 1 }),
    {
      ...DEFAULT_QUERY_OPTION,
      refetchOnMount: true,
      enabled: selectedType === 'news_disclosure',
    },
  );

  const {
    isLoading: newsSGDCKLoading,
    // error: newsSGDCKError,
    data: newsSGDCKData,
  } = useQuery(
    ['GetHomeNewsSGDCK', i18n.language],
    () => getSGDCKNewsDataService({ limit: 10, outstanding: 1 }),
    {
      ...DEFAULT_QUERY_OPTION,
      refetchOnMount: true,
      enabled: selectedType === 'news_sgdck',
    },
  );

  const newsList = useMemo(() => {
    switch (selectedType) {
      case 'news':
        if (newsData) {
          return newsData.data.map((val) => ({
            title: val.title,
            href: `${getStaticURL('NEWS_VCBS_DETAIL', language)}/${val.slug}`,
            date: formatDateDDMMYYYY(val.createdAt),
          }));
        }
        return [];
      case 'news_disclosure':
        if (newsDisclousureData) {
          return newsDisclousureData.data.map((val) => ({
            title: val.title,
            href: `${getStaticURL('NEWS_DISCLOSURE_DETAIL', language)}/${val.slug}`,
            date: formatDateDDMMYYYY(val.implementDate),
          }));
        }
        return [];

      default:
        if (newsSGDCKData) {
          return newsSGDCKData.data.map((val) => ({
            title: val.title,
            href: `${getStaticURL('NEWS_SGDCK_DETAIL', language)}/${val.slug}`,
            date: formatDateDDMMYYYY(val.releaseDate),
          }));
        }
        return [];
    }
  }, [
    newsData,
    language,
    selectedType,
    newsDisclousureData,
    newsSGDCKData,
  ]);

  return (
    <Section className="p-home_newsList">
      <Container>
        <NewsListHome
          title={t('general.news_title')}
          viewMoreText={blocks.link.text}
          href={blocks.link.url}
          newsList={newsList}
          target={blocks.link.target}
          radioList={radioList}
          selectedType={selectedType}
          handleSetSelectedType={handleSetSelectedType}
          loading={newsLoading || newsDisclousureLoading || newsSGDCKLoading}
        />
      </Container>
    </Section>
  );
};

export default NewsList;
